import { normalizeBarcode } from "../food-sources/open-food-facts/mapper";
import { BarcodeScanError, type BarcodeScanner, type BarcodeScanOptions } from "./barcode-scanner";
import { stopCamera } from "./browser-barcode-detector";

/** Lazily loaded so the decoder does not add weight to the normal app shell. */
export class ZxingBarcodeScanner implements BarcodeScanner {
  async isSupported(): Promise<boolean> {
    return Boolean(navigator.mediaDevices?.getUserMedia);
  }

  async scan(video: HTMLVideoElement, options: BarcodeScanOptions = {}): Promise<string> {
    if (!await this.isSupported()) throw new BarcodeScanError("Camera scanning is unavailable", "unsupported");
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 60_000);
    const signal = combineSignals(options.signal, timeout);
    let controls: { stop(): void } | undefined;
    try {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      const reader = new BrowserMultiFormatReader();
      return await new Promise<string>((resolve, reject) => {
        const onAbort = () => reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
        signal.addEventListener("abort", onAbort, { once: true });
        void reader.decodeFromConstraints(
          { audio: false, video: { facingMode: { ideal: "environment" } } },
          video,
          (result, _error, scannerControls) => {
            controls = scannerControls;
            const barcode = result ? normalizeBarcode(result.getText()) : undefined;
            if (barcode) {
              signal.removeEventListener("abort", onAbort);
              resolve(barcode);
            }
          },
        ).then((scannerControls) => { controls = scannerControls; }).catch(reject);
      });
    } catch (error) {
      if (signal.aborted) throw signal.reason ?? new DOMException("Aborted", "AbortError");
      if (error instanceof DOMException && ["NotAllowedError", "SecurityError"].includes(error.name)) {
        throw new BarcodeScanError("Camera permission was not granted", "permission-denied");
      }
      if (error instanceof BarcodeScanError) throw error;
      throw new BarcodeScanError("The barcode scanner could not start", "camera-error");
    } finally {
      controls?.stop();
      stopCamera(video);
    }
  }
}

function combineSignals(...signals: Array<AbortSignal | undefined>): AbortSignal {
  const available = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (typeof AbortSignal.any === "function") return AbortSignal.any(available);
  const controller = new AbortController();
  for (const signal of available) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

